import {
    type ApiError,
    type ApiOrganizationMemberProfiles,
    type ApiReassignUserDashboardsResponse,
    type ApiReassignUserSchedulersResponse,
    type ApiUserDashboardsSummaryResponse,
    type ApiUserSchedulersSummaryResponse,
    type KnexPaginateArgs,
    type OrganizationMemberSort,
} from '@lightdash/common';
import {
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
    type UseInfiniteQueryOptions,
} from '@tanstack/react-query';
import Fuse from 'fuse.js';
import { useMemo } from 'react';
import { type LightdashApi } from '../api';
import { useLightdashApi } from '../providers/LightdashApi/useLightdashApi';
import useToaster from './toaster/useToaster';
import useQueryError from './useQueryError';

type OrganizationUser =
    ApiOrganizationMemberProfiles['results']['data'][number];

const getOrganizationUsersQuery = async (
    lightdashApi: LightdashApi,
    params?: {
        includeGroups?: number;
        paginateArgs?: KnexPaginateArgs;
        searchQuery?: string;
        projectUuid?: string;
        googleOidcOnly?: boolean;
        sort?: OrganizationMemberSort;
    },
) => {
    const urlParams = new URLSearchParams({
        ...(params?.paginateArgs
            ? {
                  page: String(params.paginateArgs.page),
                  pageSize: String(params.paginateArgs.pageSize),
              }
            : {}),
        ...(params?.includeGroups
            ? { includeGroups: String(params?.includeGroups) }
            : {}),
        ...(params?.searchQuery ? { searchQuery: params.searchQuery } : {}),
        ...(params?.projectUuid ? { projectUuid: params.projectUuid } : {}),
        ...(params?.googleOidcOnly
            ? { googleOidcOnly: String(params.googleOidcOnly) }
            : {}),
        ...(params?.sort
            ? {
                  sortBy: params.sort.sortBy,
                  sortDirection: params.sort.sortDirection,
              }
            : {}),
    }).toString();

    return lightdashApi<ApiOrganizationMemberProfiles['results']>({
        url: `/org/users${urlParams ? `?${urlParams}` : ''}`,
        method: 'GET',
        body: undefined,
    });
};

const deleteUserQuery = async (lightdashApi: LightdashApi, id: string) =>
    lightdashApi<null>({
        url: `/org/user/${id}`,
        method: 'DELETE',
        body: undefined,
    });

export const useOrganizationUsers = (params?: {
    searchInput?: string;
    includeGroups?: number;
    projectUuid?: string;
    enabled?: boolean;
    paginateArgs?: KnexPaginateArgs;
}) => {
    const lightdashApi = useLightdashApi();

    const setErrorResponse = useQueryError();
    return useQuery<ApiOrganizationMemberProfiles['results']['data'], ApiError>(
        {
            queryKey: [
                'organization_users',
                params?.includeGroups,
                params?.searchInput,
            ],
            queryFn: async () => {
                return (
                    await getOrganizationUsersQuery(lightdashApi, {
                        includeGroups: params?.includeGroups,
                        searchQuery: params?.searchInput,
                        projectUuid: params?.projectUuid,
                        paginateArgs: params?.paginateArgs,
                    })
                ).data;
            },
            onError: (result) => setErrorResponse(result),
            enabled: params?.enabled !== false,
            select: (data) => {
                if (params?.searchInput) {
                    return new Fuse(Object.values(data), {
                        keys: ['firstName', 'lastName', 'email', 'role'],
                        ignoreLocation: true,
                        threshold: 0.3,
                    })
                        .search(params.searchInput)
                        .map((result) => result.item);
                }
                return data;
            },
        },
    );
};

export const useOrgUsersByUuid = () => {
    const { data: orgUsers = [] } = useOrganizationUsers();

    return useMemo(
        () =>
            new Map<string, OrganizationUser>(
                orgUsers.map((orgUser) => [orgUser.userUuid, orgUser]),
            ),
        [orgUsers],
    );
};

export const useInfiniteOrganizationUsers = (
    {
        searchInput,
        includeGroups,
        pageSize,
        projectUuid,
        googleOidcOnly,
        sort,
    }: {
        searchInput?: string;
        includeGroups?: number;
        projectUuid?: string;
        pageSize: number;
        googleOidcOnly?: boolean;
        sort?: OrganizationMemberSort;
    },
    infinityQueryOpts: UseInfiniteQueryOptions<
        ApiOrganizationMemberProfiles['results'],
        ApiError
    > = {},
) => {
    const lightdashApi = useLightdashApi();

    const setErrorResponse = useQueryError();
    return useInfiniteQuery<ApiOrganizationMemberProfiles['results'], ApiError>(
        {
            queryKey: [
                'organization_users',
                includeGroups,
                pageSize,
                searchInput,
                projectUuid,
                googleOidcOnly,
                sort,
            ],
            queryFn: ({ pageParam }) => {
                return getOrganizationUsersQuery(lightdashApi, {
                    includeGroups,
                    paginateArgs: {
                        pageSize: pageSize,
                        page: pageParam ?? 1,
                    },
                    searchQuery: searchInput,
                    projectUuid,
                    googleOidcOnly,
                    sort,
                });
            },
            onError: (result) => setErrorResponse(result),
            getNextPageParam: (lastPage) => {
                if (lastPage.pagination) {
                    return lastPage.pagination.page <
                        lastPage.pagination.totalPageCount
                        ? lastPage.pagination.page + 1
                        : undefined;
                }
            },
            ...infinityQueryOpts,
        },
    );
};

export const useDeleteOrganizationUserMutation = () => {
    const lightdashApi = useLightdashApi();

    const queryClient = useQueryClient();
    const { showToastSuccess, showToastApiError } = useToaster();
    return useMutation<null, ApiError, string>(
        (id: string) => deleteUserQuery(lightdashApi, id),
        {
            mutationKey: ['organization_users_delete'],
            onSuccess: async () => {
                await queryClient.invalidateQueries(['organization_users']);
                showToastSuccess({
                    title: `Success! User was deleted.`,
                });
            },
            onError: ({ error }) => {
                showToastApiError({
                    title: `Failed to delete user`,
                    apiError: error,
                });
            },
        },
    );
};

const getUserSchedulersSummaryQuery = async (
    lightdashApi: LightdashApi,
    userUuid: string,
) =>
    lightdashApi<ApiUserSchedulersSummaryResponse['results']>({
        url: `/org/user/${userUuid}/schedulers-summary`,
        method: 'GET',
        body: undefined,
    });

export const useUserSchedulersSummary = (
    userUuid: string,
    enabled: boolean = true,
) => {
    const lightdashApi = useLightdashApi();

    const setErrorResponse = useQueryError();
    return useQuery<ApiUserSchedulersSummaryResponse['results'], ApiError>({
        queryKey: ['user_schedulers_summary', userUuid],
        queryFn: () => getUserSchedulersSummaryQuery(lightdashApi, userUuid),
        onError: (result) => setErrorResponse(result),
        enabled,
    });
};

const reassignUserSchedulersQuery = async (
    lightdashApi: LightdashApi,
    {
        userUuid,
        newOwnerUserUuid,
    }: {
        userUuid: string;
        newOwnerUserUuid: string;
    },
) =>
    lightdashApi<ApiReassignUserSchedulersResponse['results']>({
        url: `/org/user/${userUuid}/reassign-schedulers`,
        method: 'PATCH',
        body: JSON.stringify({ newOwnerUserUuid }),
    });

const getUserDashboardsSummaryQuery = async (
    lightdashApi: LightdashApi,
    userUuid: string,
) =>
    // prettier-ignore
    lightdashApi<ApiUserDashboardsSummaryResponse['results']>({ // pragma: allowlist secret
        url: `/org/user/${userUuid}/dashboards-summary`,
        method: 'GET',
        body: undefined,
    });

export const useUserDashboardsSummary = (
    userUuid: string,
    enabled: boolean = true,
) => {
    const lightdashApi = useLightdashApi();

    const setErrorResponse = useQueryError();
    return useQuery<ApiUserDashboardsSummaryResponse['results'], ApiError>({
        queryKey: ['user_dashboards_summary', userUuid],
        queryFn: () => getUserDashboardsSummaryQuery(lightdashApi, userUuid),
        onError: (result) => setErrorResponse(result),
        enabled,
    });
};

const reassignUserDashboardsQuery = async (
    lightdashApi: LightdashApi,
    {
        userUuid,
        newOwnerUserUuid,
    }: {
        userUuid: string;
        newOwnerUserUuid: string;
    },
) =>
    // prettier-ignore
    lightdashApi<ApiReassignUserDashboardsResponse['results']>({ // pragma: allowlist secret
        url: `/org/user/${userUuid}/reassign-dashboards`,
        method: 'PATCH',
        body: JSON.stringify({ newOwnerUserUuid }),
    });

export const useReassignUserDashboardsMutation = () => {
    const lightdashApi = useLightdashApi();

    const { showToastSuccess, showToastApiError } = useToaster();
    return useMutation<
        ApiReassignUserDashboardsResponse['results'],
        ApiError,
        { userUuid: string; newOwnerUserUuid: string }
    >(
        (args: { userUuid: string; newOwnerUserUuid: string }) =>
            reassignUserDashboardsQuery(lightdashApi, args),
        {
            mutationKey: ['reassign_user_dashboards'],
            onSuccess: async (data) => {
                showToastSuccess({
                    title: `Success! ${data.reassignedCount} ${
                        data.reassignedCount === 1 ? 'dashboard' : 'dashboards'
                    } transferred.`,
                });
            },
            onError: ({ error }) => {
                showToastApiError({
                    title: `Failed to transfer dashboards`,
                    apiError: error,
                });
            },
        },
    );
};

export const useReassignUserSchedulersMutation = () => {
    const lightdashApi = useLightdashApi();

    const { showToastSuccess, showToastApiError } = useToaster();
    return useMutation<
        ApiReassignUserSchedulersResponse['results'],
        ApiError,
        { userUuid: string; newOwnerUserUuid: string }
    >(
        (args: { userUuid: string; newOwnerUserUuid: string }) =>
            reassignUserSchedulersQuery(lightdashApi, args),
        {
            mutationKey: ['reassign_user_schedulers'],
            onSuccess: async (data) => {
                showToastSuccess({
                    title: `Success! ${data.reassignedCount} scheduled ${
                        data.reassignedCount === 1 ? 'delivery' : 'deliveries'
                    } reassigned.`,
                });
            },
            onError: ({ error }) => {
                showToastApiError({
                    title: `Failed to reassign scheduled deliveries`,
                    apiError: error,
                });
            },
        },
    );
};
